import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import type { TaxRegisterEntry, TaxRegisterTotals } from "../types";

interface UseTaxRegisterEntriesResult {
  entries: TaxRegisterEntry[];
  totals: TaxRegisterTotals | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useTaxRegisterEntries(
  fiscalPeriod: string,
  registerType: "sales" | "purchases",
): UseTaxRegisterEntriesResult {
  const [entries, setEntries] = useState<TaxRegisterEntry[]>([]);
  const [totals, setTotals] = useState<TaxRegisterTotals | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    if (!fiscalPeriod) return;

    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiFetch.get<{
          entries: TaxRegisterEntry[];
          totals: TaxRegisterTotals;
        }>(`/api/rcv/entries/${fiscalPeriod}/${registerType}`);

        if (!cancelled) {
          setEntries(data.entries);
          setTotals(data.totals);
        }
      } catch (err) {
        if (!cancelled) {
          // 404 means no data synced yet — not an error
          setEntries([]);
          setTotals(null);
          if (err instanceof Error && !err.message.includes("404")) {
            setError(err);
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [fiscalPeriod, registerType, fetchKey]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  return { entries, totals, isLoading, error, refetch };
}
